import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Box, Link, Typography } from '@material-ui/core';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  LoginAmplify,
  LoginAuth0,
  LoginFirebase,
  LoginJWT
} from '../../components/authentication/login';
import LoginBrandPanel from '../../components/authentication/login/LoginBrandPanel';
import useAuth from '../../hooks/useAuth';
import gtm from '../../lib/gtm';
import { getTone } from '../../theme/tones';
import { MOTION, FONT_DISPLAY } from '../../theme/tokens';

/**
 * Écran de connexion.
 *
 * Deux volets. À gauche, le panneau de marque : la matière du rail de
 * navigation, une scène 3D qui répond à la souris et une phrase. À droite,
 * le formulaire, posé sur la surface de l'application (il suit donc le
 * thème clair / sombre) : salutation selon l'heure, espace client,
 * champs, bouton. Rien d'autre.
 */

/** « Bonjour » en journée, « Bonsoir » à partir de 18 h. */
const getGreeting = () => {
  const hour = new Date().getHours();
  return hour >= 5 && hour < 18 ? 'Bonjour' : 'Bonsoir';
};

const Login = () => {
  const { platform } = useAuth();
  const { hostname } = window.location;
  const tenant = hostname.split('.')[0].toUpperCase();
  const year = new Date().getFullYear();

  useEffect(() => {
    gtm.push({ event: 'page_view' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Syndays {tenant} | Connexion</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          backgroundColor: 'background.default'
        }}
      >
        {/* ------------------------------------------------ Marque -- */}
        <LoginBrandPanel
          sx={{
            flex: { md: '0 0 54%', lg: '0 0 56%' },
            minHeight: { md: '100vh' }
          }}
        />

        {/* --------------------------------------------- Formulaire -- */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { md: '100vh' }
          }}
        >
          <Box
            component={motion.div}
            variants={MOTION.container}
            initial="hidden"
            animate="show"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              maxWidth: 460,
              mx: 'auto',
              px: { xs: 3, sm: 5 },
              pt: { xs: 5, md: 8 },
              pb: { xs: 4, md: 6 }
            }}
          >
            {/* Espace client : la même pastille « en ligne » que la barre haute. */}
            <Box
              component={motion.div}
              variants={MOTION.item}
              sx={{
                alignSelf: 'flex-start',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.4,
                py: 0.6,
                mb: 4,
                borderRadius: '999px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: getTone('emerald').base,
                  boxShadow: `0 0 0 3px ${getTone('emerald').bgStrong}`
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  color: 'text.secondary'
                }}
              >
                ESPACE
                {' '}
                {tenant}
              </Typography>
            </Box>

            <Typography
              component={motion.h1}
              variants={MOTION.item}
              sx={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.35rem' },
                letterSpacing: '-0.035em',
                lineHeight: 1.08,
                color: 'text.primary',
                m: 0
              }}
            >
              {getGreeting()}
              .
            </Typography>

            <Typography
              component={motion.p}
              variants={MOTION.item}
              sx={{
                mt: 1.4,
                mb: 3,
                color: 'text.secondary',
                fontSize: '0.98rem',
                lineHeight: 1.55
              }}
            >
              Connectez-vous pour retrouver vos sessions, vos apprenants et vos documents.
            </Typography>

            <Box
              component={motion.div}
              variants={MOTION.item}
            >
              {platform === 'Amplify' && <LoginAmplify />}
              {platform === 'Auth0' && <LoginAuth0 />}
              {platform === 'Firebase' && <LoginFirebase />}
              {platform === 'JWT' && <LoginJWT />}
            </Box>

            <Box
              component={motion.div}
              variants={MOTION.item}
              sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}
            >
              <Link
                component={RouterLink}
                to="../password-recovery"
                underline="none"
                sx={{
                  fontSize: '0.86rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: 'secondary.main' }
                }}
              >
                Mot de passe oublié ?
              </Link>
            </Box>
          </Box>

          {/* Pied de page : discret, aligné sur la colonne du formulaire. */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 460,
              mx: 'auto',
              px: { xs: 3, sm: 5 },
              pb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              color: 'text.disabled',
              fontSize: '0.72rem',
              fontWeight: 500
            }}
          >
            <Box component="span">
              ©
              {' '}
              {year}
              {' '}
              Syndays
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
              <LockOutlinedIcon sx={{ fontSize: 13 }} />
              Connexion chiffrée
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
